import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { History } from 'history';
import { AppUser } from 'Src/views/App/App';
import { Child } from 'Src/models/child';
import { SchoolEvent } from 'Src/models/event';
import { EVENT_STATUS } from 'Src/consts/event';
import { getChildren } from 'Src/helpers/service/parent/children';
import { getChildrenEvents } from 'Src/helpers/service/parent/childrenEvents';
import { getEventNameForParent } from 'Src/helpers/accessor/accessor';
import { Loader } from 'Src/components/Loader/Loader';
import { ParentPageHeader } from '../components/ParentPageHeader';
import './ParentHome.scss';
import { getEventCommunicationsDashboard } from 'Src/helpers/service/eventCommunications';

interface Props {
  user: AppUser;
  history: History;
  countBadges: {
    requests: string;
    bookings: string;
    availabilityReports: string;
    absenceRequests: string;
  };
}

interface State {
  isLoading: boolean;
  children: Child[];
  upcomingEvents: SchoolEvent[];
  error: string;
  communications: {unread: number; mentions: number; conversationsWithUnread: any[]};
}

const UPCOMING_EVENT_STATUSES = [
  EVENT_STATUS.INVITES_SENT,
  EVENT_STATUS.COLLECTING_INVITE_RESPONSE,
  EVENT_STATUS.ACCEPTED
];

function numberBadge(value: string): number {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function childInitials(child: Child): string {
  return `${child.firstName || ''} ${child.lastName || ''}`
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
}

export class ParentHome extends React.Component<Props, State> {
  state: State = { isLoading: true, children: [], upcomingEvents: [], error: '', communications: {unread: 0, mentions: 0, conversationsWithUnread: []} };

  componentDidMount() {
    const { user } = this.props;
    Promise.all([getChildren(user), getEventCommunicationsDashboard(user)]).then(([children, communications]: [Child[], any]) => {
        if (children.length === 0) return { children, upcomingEvents: [], communications };
        const now = new Date();
        const rangeEnd = new Date(now);
        rangeEnd.setMonth(rangeEnd.getMonth() + 3);
        return getChildrenEvents(user, {
          limit: 5,
          skip: 0,
          order: 'startTime ASC',
          where: {
            childIdList: children.map(child => child.id),
            startTime: { $gte: now, $lt: rangeEnd },
            status: { $in: UPCOMING_EVENT_STATUSES }
          }
        }).then(upcomingEvents => ({ children, upcomingEvents, communications }));
      }).then(({ children, upcomingEvents, communications }) => this.setState({ isLoading: false, children, upcomingEvents, communications }))
      .catch(() => this.setState({ isLoading: false, error: 'We could not load your family overview. Please try again.' }));
  }

  openEvent = (eventId: string) => {
    this.props.history.push({
      pathname: '/events/event',
      search: `id=${eventId}`,
      state: { prevRoutePath: '/home' }
    });
  };

  openFirstUnreadConversation = () => {
    const first = this.state.communications.conversationsWithUnread[0];
    if (!first || !first.eventId) return this.props.history.push('/calendar');
    this.props.history.push({ pathname: '/events/event', search: `id=${first.eventId}`, state: { prevRoutePath: '/home', initialTab: 'Communications', conversationId: first.id } });
  };

  render() {
    const { user, history, countBadges } = this.props;
    const { isLoading, children, upcomingEvents, error, communications } = this.state;
    const firstName = propz.get(user, ['profile', 'firstName'], '');
    const consentCount = numberBadge(countBadges.requests);
    const clubInviteCount = numberBadge(countBadges.bookings);
    const openActionCount = consentCount + clubInviteCount;
    const todayEventCount = upcomingEvents.filter(event => moment(event.startTime).isSame(moment(), 'day')).length;
    const childIdList = children.map(child => child.id);

    if (isLoading) return <Loader />;

    return (
      <main className="parentPage parentHome">
        <ParentPageHeader
          title={`${getGreeting()}${firstName ? `, ${firstName}` : ''}`}
          subtitle="Everything your family needs for school sport and activities, in one place."
          chips={[
            { value: children.length, label: children.length === 1 ? 'child' : 'children', tone: 'blue' },
            { value: openActionCount, label: openActionCount === 1 ? 'action to complete' : 'actions to complete', tone: openActionCount ? 'amber' : 'green' },
            { value: todayEventCount, label: todayEventCount === 1 ? 'event today' : 'events today', tone: 'slate' }
          ]}
        />

        {error ? <div className="parentHome__error">{error}</div> : null}

        <section className="parentHome__layout">
          <div className="parentHome__primary">
            {communications.unread > 0 && <section className="parentCard parentHome__section parentHome__messageCard"><div className="parentHome__sectionHeader"><div><h2>Chats</h2><p>{communications.unread} unread · {communications.mentions} mentions</p></div><span className="parentHome__count">{communications.unread}</span></div><button className="parentHome__messageCta" type="button" onClick={this.openFirstUnreadConversation}><span>Open unread chat</span><i aria-hidden="true">›</i></button></section>}
            <section className="parentCard parentHome__section" data-help-anchor="parent-home-attention">
              <div className="parentHome__sectionHeader">
                <div><h2>Needs your attention</h2><p>Requests that still need a response.</p></div>
                {openActionCount > 0 ? <span className="parentHome__count">{openActionCount}</span> : null}
              </div>
              {openActionCount === 0 ? (
                <div className="parentHome__clearState">
                  <span aria-hidden="true">✓</span>
                  <div><strong>You’re all caught up</strong><p>There are no consent requests or club invitations waiting for you.</p></div>
                </div>
              ) : (
                <div className="parentHome__actionsList">
                  {consentCount > 0 ? (
                    <button type="button" onClick={() => history.push('/consentRequests/inbox')}>
                      <span className="parentHome__actionIcon parentHome__actionIcon--amber">✓</span>
                      <span><strong>Consent requests</strong><small>{consentCount} {consentCount === 1 ? 'request needs' : 'requests need'} your response</small></span>
                      <b>{consentCount}</b><i aria-hidden="true">›</i>
                    </button>
                  ) : null}
                  {clubInviteCount > 0 ? (
                    <button type="button" onClick={() => history.push('/clubBookings/inbox')}>
                      <span className="parentHome__actionIcon parentHome__actionIcon--blue">C</span>
                      <span><strong>Club invitations</strong><small>{clubInviteCount} {clubInviteCount === 1 ? 'place is' : 'places are'} waiting for a decision</small></span>
                      <b>{clubInviteCount}</b><i aria-hidden="true">›</i>
                    </button>
                  ) : null}
                </div>
              )}
            </section>

            <section className="parentCard parentHome__section" data-help-anchor="parent-home-events">
              <div className="parentHome__sectionHeader">
                <div><h2>Next events</h2><p>Your family schedule for the next three months.</p></div>
                <button className="parentHome__textButton" type="button" onClick={() => history.push('/calendar')}>View calendar</button>
              </div>
              {upcomingEvents.length === 0 ? (
                <div className="parentHome__empty"><strong>No upcoming events</strong><span>New fixtures and activities will appear here when the school publishes them.</span></div>
              ) : (
                <div className="parentHome__events">
                  {upcomingEvents.map(event => {
                    const eventPlayers = propz.get(event, ['players'], []);
                    const participating = children.filter(child => eventPlayers.some(player => String(player.userId) === child.id));
                    return (
                      <button type="button" key={event.id} onClick={() => this.openEvent(event.id)}>
                        <time><strong>{moment(event.startTime).format('DD')}</strong><span>{moment(event.startTime).format('MMM')}</span></time>
                        <span className="parentHome__eventMain">
                          <strong>{getEventNameForParent(event, childIdList)}</strong>
                          <small>{moment(event.startTime).format('ddd, D MMM · HH:mm')}{participating.length ? ` · ${participating.map(child => child.firstName).join(', ')}` : ''}</small>
                        </span>
                        <i aria-hidden="true">›</i>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <aside className="parentHome__secondary">
            <section className="parentCard parentHome__section" data-help-anchor="parent-home-children">
              <div className="parentHome__sectionHeader"><div><h2>Your children</h2><p>Profiles linked to this account.</p></div></div>
              <div className="parentHome__children">
                {children.map(child => (
                  <button type="button" key={child.id} onClick={() => history.push('/children')}>
                    {child.avatar ? <span className="parentHome__avatar" style={{ backgroundImage: `url(${child.avatar})` }} /> : <span className="parentHome__avatar parentHome__avatar--letters">{childInitials(child)}</span>}
                    <span><strong>{child.firstName} {child.lastName}</strong><small>{child.formName || propz.get(child, ['form', 'name'], '') || child.schoolName || 'Student'}</small></span>
                    <i aria-hidden="true">›</i>
                  </button>
                ))}
              </div>
            </section>

            <section
              className="parentCard parentHome__section parentHome__updates"
              data-help-anchor="parent-home-updates"
            >
              <div className="parentHome__sectionHeader"><div><h2>Recent updates</h2><p>Requests you have already submitted.</p></div></div>
              <button type="button" onClick={() => history.push('/availabilityReports/outbox')}><span>Availability reports</span><strong>{numberBadge(countBadges.availabilityReports)}</strong></button>
              <button type="button" onClick={() => history.push('/absenceRequests/outbox')}><span>Absence requests</span><strong>{numberBadge(countBadges.absenceRequests)}</strong></button>
            </section>
          </aside>
        </section>
      </main>
    );
  }
}
